import type { DragEvent } from 'react';

/**
 * Files dragged from the PC and dropped on the page: plain files, and whole folders read with the folders inside them
 * (each file with its folder path, e.g. "Drawings/Power"). Browsers that can't read folders give the plain files.
 */
export async function droppedFiles(dt: DataTransfer): Promise<{ file: File; dir: string }[]> {
  const entries = [...dt.items].map((it) => (it.kind === 'file' ? it.webkitGetAsEntry?.() ?? null : null));
  if (!entries.some(Boolean)) return [...dt.files].map((file) => ({ file, dir: '' }));
  const out: { file: File; dir: string }[] = [];
  const walk = async (entry: FileSystemEntry, dir: string): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((ok, ko) => (entry as FileSystemFileEntry).file(ok, ko));
      out.push({ file, dir });
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      const sub = [dir, entry.name].filter(Boolean).join('/');
      // A folder is read in batches until an empty one.
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((ok, ko) => reader.readEntries(ok, ko));
        if (!batch.length) break;
        for (const child of batch) await walk(child, sub);
      }
    }
  };
  for (const entry of entries) if (entry) await walk(entry, '');
  return out;
}

/** Drag-over / leave / drop handlers for a drop zone (the zone shows "Drop files here" while files hover over it). */
export function dropZone(setOver: (v: boolean) => void, onFiles: (e: DragEvent<HTMLElement>) => void) {
  const hasFiles = (e: DragEvent<HTMLElement>) => [...e.dataTransfer.types].includes('Files');
  return {
    onDragOver: (e: DragEvent<HTMLElement>) => { if (hasFiles(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setOver(true); } },
    onDragLeave: (e: DragEvent<HTMLElement>) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false); },
    onDrop: (e: DragEvent<HTMLElement>) => { if (hasFiles(e)) onFiles(e); else setOver(false); },
  };
}
